import { useState } from 'react';

function UpdatesForm() {
  const [data, setData] = useState({ name: '', email: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setData({ ...data, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const body = new URLSearchParams();
      Object.entries(data).forEach(([k, v]) => body.append(k, v));
      await fetch('https://readdy.ai/api/form/d7c4ms8r4h7u93gnq2t0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div id="updates-form" className="bg-white rounded-2xl border border-brand-100 p-8">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 flex items-center justify-center bg-brand-50 rounded-xl">
          <i className="ri-mail-send-line text-brand-500 text-xl"></i>
        </div>
        <div>
          <h3 className="text-lg font-bold text-gray-900 font-heading">Sign Up for Updates</h3>
          <p className="text-xs text-gray-400 font-body">Research news, community events, resources</p>
        </div>
      </div>

      {submitted ? (
        <div className="text-center py-8">
          <div className="w-12 h-12 flex items-center justify-center bg-brand-50 rounded-full mx-auto mb-3">
            <i className="ri-check-line text-2xl text-brand-500"></i>
          </div>
          <p className="font-semibold text-gray-900 font-heading">You&apos;re subscribed!</p>
          <p className="text-sm text-gray-400 font-body mt-1">We&apos;ll be in touch with meaningful updates.</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5 font-body">Your Name</label>
            <input
              type="text"
              name="name"
              value={data.name}
              onChange={handleChange}
              placeholder="Full name"
              required
              className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100 transition-colors font-body"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5 font-body">Email Address</label>
            <input
              type="email"
              name="email"
              value={data.email}
              onChange={handleChange}
              placeholder="you@example.com"
              required
              className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-brand-400 focus:ring-2 focus:ring-brand-100 transition-colors font-body"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3 bg-brand-500 text-white font-semibold rounded-full hover:bg-brand-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 font-body text-sm"
          >
            {submitting ? 'Subscribing...' : 'Subscribe to Updates'}
          </button>
          <p className="text-xs text-gray-400 text-center font-body">No spam, ever. Unsubscribe anytime.</p>
        </form>
      )}
    </div>
  );
}

function ResourceForm() {
  const [data, setData] = useState({ name: '', email: '', title: '', url: '', category: '', description: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setData({ ...data, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (data.description.length > 500) return;
    setSubmitting(true);
    try {
      const body = new URLSearchParams();
      Object.entries(data).forEach(([k, v]) => body.append(k, v));
      await fetch('https://readdy.ai/api/form/d7c4vd2qofe8vli40ua0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div id="resource-form" className="bg-white rounded-2xl border border-green-100 p-8">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 flex items-center justify-center bg-green-50 rounded-xl">
          <i className="ri-share-line text-green-600 text-xl"></i>
        </div>
        <div>
          <h3 className="text-lg font-bold text-gray-900 font-heading">Share a Resource</h3>
          <p className="text-xs text-gray-400 font-body">Help the community by sharing useful links or materials</p>
        </div>
      </div>

      {submitted ? (
        <div className="text-center py-8">
          <div className="w-12 h-12 flex items-center justify-center bg-green-50 rounded-full mx-auto mb-3">
            <i className="ri-check-line text-2xl text-green-600"></i>
          </div>
          <p className="font-semibold text-gray-900 font-heading">Resource submitted!</p>
          <p className="text-sm text-gray-400 font-body mt-1">Thank you for contributing to the community.</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5 font-body">Your Name</label>
              <input
                type="text"
                name="name"
                value={data.name}
                onChange={handleChange}
                placeholder="Full name"
                required
                className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-green-400 focus:ring-2 focus:ring-green-100 transition-colors font-body"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5 font-body">Email Address</label>
              <input
                type="email"
                name="email"
                value={data.email}
                onChange={handleChange}
                placeholder="you@example.com"
                required
                className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-green-400 focus:ring-2 focus:ring-green-100 transition-colors font-body"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5 font-body">Resource Title</label>
            <input
              type="text"
              name="title"
              value={data.title}
              onChange={handleChange}
              placeholder="Name of the article, website, or document"
              required
              className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-green-400 focus:ring-2 focus:ring-green-100 transition-colors font-body"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5 font-body">Resource URL</label>
            <input
              type="url"
              name="url"
              value={data.url}
              onChange={handleChange}
              placeholder="https://..."
              className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-green-400 focus:ring-2 focus:ring-green-100 transition-colors font-body"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5 font-body">Category</label>
            <select
              name="category"
              value={data.category}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-800 focus:outline-none focus:border-green-400 focus:ring-2 focus:ring-green-100 transition-colors cursor-pointer font-body"
            >
              <option value="">Select a category</option>
              <option value="medical">Medical / Clinical</option>
              <option value="research">Research / Science</option>
              <option value="support">Family Support</option>
              <option value="genetics">Genetics & Diagnosis</option>
              <option value="therapy">Therapy & Rehabilitation</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5 font-body">Why is this helpful?</label>
            <textarea
              name="description"
              value={data.description}
              onChange={handleChange}
              placeholder="Briefly describe why this resource is useful for the CSCF community..."
              rows={3}
              maxLength={500}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-green-400 focus:ring-2 focus:ring-green-100 transition-colors resize-none font-body"
            />
            <p className={`text-xs mt-1 text-right font-body ${data.description.length >= 500 ? 'text-red-400' : 'text-gray-400'}`}>
              {data.description.length}/500
            </p>
          </div>
          <button
            type="submit"
            disabled={submitting || data.description.length > 500}
            className="w-full py-3 bg-green-600 text-white font-semibold rounded-full hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 font-body text-sm"
          >
            {submitting ? 'Submitting...' : 'Submit Resource'}
          </button>
        </form>
      )}
    </div>
  );
}

function StoryForm() {
  const [data, setData] = useState({ name: '', email: '', role: '', story: '' });
  const [disclaimer, setDisclaimer] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setData({ ...data, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (data.story.length > 500 || !disclaimer) return;
    setSubmitting(true);
    try {
      const body = new URLSearchParams();
      Object.entries(data).forEach(([k, v]) => body.append(k, v));
      body.append('disclaimer_accepted', 'yes');
      await fetch('https://readdy.ai/api/form/d7c4ms8r4h7u93gnq2tg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div id="story-form" className="bg-white rounded-2xl border border-accent-100 p-8">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 flex items-center justify-center bg-accent-50 rounded-xl">
          <i className="ri-quill-pen-line text-accent-500 text-xl"></i>
        </div>
        <div>
          <h3 className="text-lg font-bold text-gray-900 font-heading">Share Your Story</h3>
          <p className="text-xs text-gray-400 font-body">Your experience helps other families feel less alone</p>
        </div>
      </div>

      {submitted ? (
        <div className="text-center py-8">
          <div className="w-12 h-12 flex items-center justify-center bg-accent-50 rounded-full mx-auto mb-3">
            <i className="ri-check-line text-2xl text-accent-500"></i>
          </div>
          <p className="font-semibold text-gray-900 font-heading">Thank you for sharing!</p>
          <p className="text-sm text-gray-400 font-body mt-1">Your story means a lot to this community.</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5 font-body">Your Name</label>
              <input
                type="text"
                name="name"
                value={data.name}
                onChange={handleChange}
                placeholder="Full name"
                required
                className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-colors font-body"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5 font-body">Email Address</label>
              <input
                type="email"
                name="email"
                value={data.email}
                onChange={handleChange}
                placeholder="you@example.com"
                required
                className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-colors font-body"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5 font-body">I am a...</label>
            <select
              name="role"
              value={data.role}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-800 focus:outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-colors cursor-pointer font-body"
            >
              <option value="">Select your role</option>
              <option value="parent">Parent / Caregiver</option>
              <option value="patient">Person with CSCF</option>
              <option value="medical">Medical Professional</option>
              <option value="researcher">Researcher</option>
              <option value="supporter">Supporter / Advocate</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5 font-body">Your Story</label>
            <textarea
              name="story"
              value={data.story}
              onChange={handleChange}
              placeholder="Share your experience with CSCF — your diagnosis journey, what has helped, what you wish you had known..."
              rows={5}
              maxLength={500}
              required
              className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-accent-400 focus:ring-2 focus:ring-accent-100 transition-colors resize-none font-body"
            />
            <p className={`text-xs mt-1 text-right font-body ${data.story.length >= 500 ? 'text-red-400' : 'text-gray-400'}`}>
              {data.story.length}/500
            </p>
          </div>

          {/* Disclaimer checkbox */}
          <label className="flex items-start gap-3 cursor-pointer group">
            <div className="relative mt-0.5 flex-shrink-0">
              <input
                type="checkbox"
                checked={disclaimer}
                onChange={(e) => setDisclaimer(e.target.checked)}
                className="sr-only"
              />
              <div
                onClick={() => setDisclaimer(!disclaimer)}
                className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors cursor-pointer ${
                  disclaimer ? 'bg-accent-500 border-accent-500' : 'border-gray-300 bg-white'
                }`}
              >
                {disclaimer && <i className="ri-check-line text-white text-xs"></i>}
              </div>
            </div>
            <p className="text-xs text-gray-500 font-body leading-relaxed">
              I understand that my story <strong className="text-gray-700">may be featured on this website</strong> to help raise awareness about CSCF, and that I <strong className="text-gray-700">may be contacted by a member of our team</strong> to learn more about my experience. My personal contact information will never be shared publicly.
            </p>
          </label>

          <button
            type="submit"
            disabled={submitting || data.story.length > 500 || !disclaimer}
            className="w-full py-3 bg-accent-500 text-white font-semibold rounded-full hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 font-body text-sm"
          >
            {submitting ? 'Submitting...' : 'Submit My Story'}
          </button>
        </form>
      )}
    </div>
  );
}

export default function CommunityForms() {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-8">
        <div className="text-center mb-14">
          <div className="inline-block px-4 py-1.5 bg-brand-50 border border-brand-100 rounded-full mb-4">
            <span className="text-xs font-semibold text-brand-600 tracking-widest font-heading">TAKE ACTION</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-heading">
            Join Us Today
          </h2>
          <p className="text-gray-500 text-base max-w-xl mx-auto font-body leading-relaxed">
            Sign up for updates, share your story, or contribute a resource — every connection strengthens this community.
          </p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <UpdatesForm />
          <StoryForm />
          <ResourceForm />
        </div>
      </div>
    </section>
  );
}
